import VideoPreview from "@/src/components/posts/videos/video-preview";
import { GallerySlash } from "iconsax-react-nativejs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Pressable,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useTheme } from "@/src/styles/theme-provider";
import VideoLightbox from "./video-lightbox";

type Props = Readonly<{
  videos: string[];
  isLoading: boolean;
  isError: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  onRetry?: () => void;
  isOwnProfile?: boolean;
}>;

const ProfileVideoGrid = ({
  videos,
  isLoading,
  isError,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onRetry,
  isOwnProfile,
}: Props) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (isLoading) {
    return (
      <View className="items-center py-8">
        <ActivityIndicator size="small" />
      </View>
    );
  }

  if (isError) {
    return (
      <View className="flex-1 items-center justify-center gap-3 py-8">
        <Text className="text-sm text-slate">
          {t("ERROR_COULD_NOT_LOAD_VIDEOS")}
        </Text>
        <TouchableOpacity
          onPress={onRetry}
          activeOpacity={0.7}
          className="rounded-full border border-primary px-5 py-2 hover:bg-ash-3"
        >
          <Text className="text-primary text-sm font-medium">
            {t("BTN_TRY_AGAIN")}
          </Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (!videos.length) {
    return (
      <View className="flex-1 items-center justify-center gap-2 p-10">
        <GallerySlash size={40} color={themeVars["--color-slate-default"] as string} />
        <Text className="text-base font-semibold text-onyx">
          {t(isOwnProfile ? "LABEL_NO_VIDEOS_YET" : "LABEL_USER_NO_VIDEOS")}
        </Text>
        <Text className="text-sm text-slate text-center">
          {t(
            isOwnProfile
              ? "LABEL_VIDEOS_EMPTY_DESC"
              : "LABEL_USER_VIDEOS_EMPTY_DESC",
          )}
        </Text>
      </View>
    );
  }

  return (
    <View className="flex-1">
      {/* Header */}
      <View className="mx-4 mt-4 mb-3">
        <Text className="text-base font-bold text-onyx">
          {t("LABEL_VIDEOS")}
        </Text>
        <Text className="text-xs text-slate mt-0.5">
          {videos.length}{" "}
          {videos.length === 1 ? t("LABEL_VIDEO") : t("LABEL_VIDEOS")}
        </Text>
      </View>

      <View className="h-px bg-surface-variant" />

      {/* Grid */}
      <View className="flex-row flex-wrap gap-0.5 mt-0.5">
        {videos.map((url, index) => (
          <View
            key={`${url}-${index}`}
            style={{ width: "33.33%", aspectRatio: 1 }}
          >
            <VideoPreview uri={url} width="100%" height="100%" />
            <Pressable
              className="absolute inset-0"
              onPress={() => setLightboxIndex(index)}
            />{" "}
          </View>
        ))}
      </View>

      {/* Load more */}
      {hasNextPage && (
        <TouchableOpacity
          onPress={onLoadMore}
          disabled={isFetchingNextPage}
          activeOpacity={0.7}
          className="items-center py-4 hover:bg-ash-3 hover:rounded-lg"
        >
          <Text className="text-primary text-sm font-medium">
            {t(isFetchingNextPage ? "LABEL_LOADING" : "BTN_LOAD_MORE")}
          </Text>
        </TouchableOpacity>
      )}

      <View className="h-4" />

      {/* Fullscreen video modal */}
      {lightboxIndex !== null && (
        <VideoLightbox
          videos={videos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </View>
  );
};

export default ProfileVideoGrid;
